import {
  createContext,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
} from 'react';
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Drawer — sheet lateral / inferior accesible (DESIGN-SYSTEM §3.9).
 *
 * Compound API:
 *  - <Drawer.Root open onOpenChange title side size>
 *      <Drawer.Header>...</Drawer.Header>
 *      <Drawer.Body>...</Drawer.Body>
 *      <Drawer.Footer>...</Drawer.Footer>
 *    </Drawer.Root>
 *
 * - Backdrop violeta `primary-900/60` con fade.
 * - Animacion slide segun `side` (right/left/bottom), respeta `prefers-reduced-motion`.
 * - ESC cierra; foco trap minimo; scroll del body bloqueado.
 * - ARIA: role="dialog" + aria-modal + aria-labelledby + aria-describedby.
 */
export type DrawerSide = 'right' | 'left' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'full';

export interface DrawerRootProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Titulo accesible (obligatorio para a11y). Si quieres ocultarlo, usa `hideTitle`. */
  title: ReactNode;
  description?: ReactNode;
  side?: DrawerSide;
  size?: DrawerSize;
  /** Si true (default), click en backdrop cierra. */
  closeOnOverlayClick?: boolean;
  /** Si true (default), ESC cierra. */
  closeOnEscape?: boolean;
  /** Si true, oculta el titulo visualmente (queda en aria). */
  hideTitle?: boolean;
  children: ReactNode;
}

interface DrawerContextValue {
  titleId: string;
  descriptionId: string;
  onClose: () => void;
  side: DrawerSide;
}

const DrawerContext = createContext<DrawerContextValue | null>(null);

function useDrawerContext(component: string): DrawerContextValue {
  const ctx = useContext(DrawerContext);
  if (!ctx) {
    throw new Error(`${component} debe usarse dentro de <Drawer.Root>.`);
  }
  return ctx;
}

// Selector de elementos enfocables (foco trap minimo).
const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

// Anchos/alturas por lado y tamano. Para `bottom`, size aplica a max-height.
//
// Responsive: usamos `w-full max-w-[N]px` (no `w-[N]px` fijo) para que en
// viewports más estrechos que el `max-w` el drawer se clampe al ancho real
// disponible y no desborde fuera de la pantalla. En desktop (viewport ≥ N)
// el comportamiento queda idéntico al ancho fijo original. Caso crítico que
// motivó el cambio: editor de plantillas de correo (size="lg") en 400 px,
// donde el ancho fijo 560 px dejaba el formulario completo fuera del viewport.
const sizeByLateral: Record<Exclude<DrawerSize, 'full'>, string> = {
  sm: 'w-full max-w-[320px]',
  md: 'w-full max-w-[420px]',
  lg: 'w-full max-w-[560px]',
};
const sizeByBottom: Record<Exclude<DrawerSize, 'full'>, string> = {
  sm: 'max-h-[40vh]',
  md: 'max-h-[60vh]',
  lg: 'max-h-[80vh]',
};

function panelDimensions(side: DrawerSide, size: DrawerSize): string {
  if (side === 'bottom') {
    return size === 'full' ? 'w-full max-h-[95vh]' : cn('w-full', sizeByBottom[size]);
  }
  return size === 'full'
    ? 'w-full sm:w-[90vw] max-w-[640px] h-full'
    : cn(sizeByLateral[size], 'h-full');
}

function panelPosition(side: DrawerSide): string {
  if (side === 'right') return 'right-0 top-0 bottom-0';
  if (side === 'left') return 'left-0 top-0 bottom-0';
  return 'left-0 right-0 bottom-0';
}

function panelRadius(side: DrawerSide): string {
  if (side === 'bottom') return 'rounded-t-lg';
  if (side === 'right') return 'rounded-l-lg';
  return 'rounded-r-lg';
}

// Variants segun `side`. En reduced-motion solo opacity.
function buildPanelVariants(side: DrawerSide, reduce: boolean | null): Variants {
  if (reduce) {
    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0 } },
      exit: { opacity: 0, transition: { duration: 0 } },
    };
  }
  const ease = [0.22, 1, 0.36, 1] as const;
  const enter = { duration: 0.25, ease };
  const out = { duration: 0.15, ease };

  if (side === 'right') {
    return {
      hidden: { opacity: 0, x: '100%' },
      visible: { opacity: 1, x: 0, transition: enter },
      exit: { opacity: 0, x: '100%', transition: out },
    };
  }
  if (side === 'left') {
    return {
      hidden: { opacity: 0, x: '-100%' },
      visible: { opacity: 1, x: 0, transition: enter },
      exit: { opacity: 0, x: '-100%', transition: out },
    };
  }
  return {
    hidden: { opacity: 0, y: '100%' },
    visible: { opacity: 1, y: 0, transition: enter },
    exit: { opacity: 0, y: '100%', transition: out },
  };
}

function DrawerRoot({
  open,
  onOpenChange,
  title,
  description,
  side = 'right',
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  hideTitle = false,
  children,
}: DrawerRootProps) {
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  // Refs latches: mantienen los callbacks/flags vivos SIN forzar al useEffect
  // a re-ejecutarse en cada render del consumidor. Si `onOpenChange` es
  // inline en el JSX del padre (caso común), `handleClose` cambia cada
  // render — incluirlo en el dep array del scroll-lock effect provocaba
  // que el cleanup se disparara en cada keystroke, robando el foco del
  // input activo (`previouslyFocused.current?.focus?.()`). El fix es leer
  // del ref dentro del listener, dejando el effect dependiente solo de `open`.
  const handleCloseRef = useRef(handleClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  useEffect(() => {
    handleCloseRef.current = handleClose;
    closeOnEscapeRef.current = closeOnEscape;
  });

  // Scroll lock + ESC + retorno de foco. Solo depende de `open` para que el
  // cleanup (que devuelve el foco al elemento previo) corra SOLO al cerrar.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: globalThis.KeyboardEvent) => {
      if (closeOnEscapeRef.current && event.key === 'Escape') {
        event.stopPropagation();
        handleCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Foco inicial al abrir.
  useEffect(() => {
    if (!open) return;
    const node = panelRef.current;
    if (!node) return;
    const focusables = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusables[0] ?? node).focus({ preventScroll: true });
  }, [open]);

  // Foco trap: ciclar Tab dentro del panel.
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const node = panelRef.current;
    if (!node) return;
    const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('disabled'),
    );
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const panelVariants = buildPanelVariants(side, reduceMotion);
  const backdropVariants: Variants = reduceMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const } },
        exit: { opacity: 0, transition: { duration: 0.15, ease: [0.22, 1, 0.36, 1] as const } },
      };

  const ctxValue: DrawerContextValue = {
    titleId,
    descriptionId,
    onClose: handleClose,
    side,
  };

  return (
    <DrawerContext.Provider value={ctxValue}>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-modal" role="presentation">
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-primary-900/60"
              aria-hidden="true"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={backdropVariants}
              onClick={() => closeOnOverlayClick && handleClose()}
            />

            {/* Panel */}
            <motion.div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              aria-describedby={description ? descriptionId : undefined}
              tabIndex={-1}
              onKeyDown={handleKeyDown}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={panelVariants}
              className={cn(
                'absolute bg-cream-100 text-primary-900 shadow-elev3',
                'flex flex-col overflow-hidden',
                panelPosition(side),
                panelDimensions(side, size),
                panelRadius(side),
              )}
            >
              {/* Titulo SR-only siempre presente para a11y; el header visible es opcional. */}
              <h2
                id={titleId}
                className={cn('font-display text-h3 text-primary-900', hideTitle && 'sr-only')}
                // Si el consumidor renderiza su propio Drawer.Header,
                // este nodo se mantiene sr-only para no duplicar visualmente.
                hidden={!hideTitle}
              >
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className="sr-only" hidden>
                  {description}
                </p>
              )}
              {children}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </DrawerContext.Provider>
  );
}

export interface DrawerHeaderProps {
  children?: ReactNode;
  className?: string;
  /** Si true (default), muestra el boton de cerrar. */
  showClose?: boolean;
}

function DrawerHeader({ children, className, showClose = true }: DrawerHeaderProps) {
  const { onClose } = useDrawerContext('Drawer.Header');
  return (
    <header
      className={cn(
        'flex items-start justify-between gap-4',
        'p-6 border-b border-cream-200',
        className,
      )}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {showClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className={cn(
            'shrink-0 inline-flex items-center justify-center',
            'h-8 w-8 rounded text-primary-900',
            'hover:bg-cream-200 transition-colors duration-2 ease-out',
          )}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </header>
  );
}

export interface DrawerBodyProps {
  children?: ReactNode;
  className?: string;
}

function DrawerBody({ children, className }: DrawerBodyProps) {
  // Validamos contexto para detectar uso fuera de Root.
  useDrawerContext('Drawer.Body');
  return <div className={cn('flex-1 overflow-y-auto p-6', className)}>{children}</div>;
}

export interface DrawerFooterProps {
  children?: ReactNode;
  className?: string;
}

function DrawerFooter({ children, className }: DrawerFooterProps) {
  useDrawerContext('Drawer.Footer');
  return (
    <footer
      className={cn(
        'p-6 border-t border-cream-200 bg-cream-50',
        'flex items-center justify-end gap-3',
        className,
      )}
    >
      {children}
    </footer>
  );
}

/** Compound export. */
export const Drawer = {
  Root: DrawerRoot,
  Header: DrawerHeader,
  Body: DrawerBody,
  Footer: DrawerFooter,
};
